import { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  submitPatchJobs,
  pollPatchJobStatus,
  pollMultiplePatchJobs,
  PatchJobStatusResponse,
  JobResponse,
  AggregatedPatchJobStatus,
} from '../../../services/api';
import { clearPendingChanges, patchDataInvalidated, selectPendingChanges } from '../patchesSlice';
import { mapPatchOperations } from '../utils/mapPatchOperations';
import { resolveOperationPlan, OperationPlanGroup } from '../utils/resolveOperationPlan';
import { promisePool } from '../../../utils/promisePool';
import { AppDispatch } from '../../../app/store';
import { useUser } from '../../../services/auth/auth.service';

/** How many job requests are in flight at once when a submission fans out across scope groups. */
const SUBMIT_CONCURRENCY = 4;

// Matches backend PatchJobRequestDto contract
export interface PatchJobRequestDto {
  patchIds: string[];
  setOperations?: Array<{
    setVariantPrice?: {
      variantId: string;
      authoringPrice: {
        amount: number;
      };
    };
    setBundlePrice?: {
      bundleId: string;
      authoringPrice: {
        amount: number;
      };
    };
    setNamedPriceAmount?: {
      namedPriceId: string;
      amount?: number;
    };
    setModifierWeightPrice?: {
      variantId: string;
      slotId: string;
      modifierId: string;
      weightId: string;
      authoringPrice: {
        amount: number;
      };
    };
    setModifierPriceRanges?: {
      variantId: string;
      slotId: string;
      weightPriceRanges: Array<{
        rangeStart: number;
        rangeEnd: number;
        authoringPrice?: {
          amount: number;
        };
        applyModifierWeightUpcharge: boolean;
      }>;
    };
    setModifierWeightIsExcludedFromVariant?: {
      variantId: string;
      slotId: string;
      modifierId: string;
      weightId: string;
      isExcludedFromVariant?: boolean;
    };
  }>;
  removeOperations?: Array<{
    removeBundle?: {
      bundleId: string;
    };
    removeProduct?: {
      productId: string;
    };
    removeVariant?: {
      variantId: string;
    };
    removeModifier?: {
      modifierId: string;
    };
    removeModifierFromSlot?: {
      slotId: string;
      modifierId: string;
    };
    removeWeightFromSlotModifier?: {
      slotId: string;
      modifierId: string;
      weightId: string;
    };
    /**
     * Removing an operation is expressed as a reference to it - identifiers only, no value. Any set
     * operation may also appear here in that reference form; these are the shapes in use today.
     */
    setNamedPriceAmount?: {
      namedPriceId: string;
    };
    setVariantPrice?: {
      variantId: string;
    };
    setBundlePrice?: {
      bundleId: string;
    };
    setModifierPriceRanges?: {
      variantId: string;
      slotId: string;
    };
    setModifierWeightPrice?: {
      variantId: string;
      slotId: string;
      modifierId: string;
      weightId: string;
    };
    setModifierWeightIsExcludedFromVariant?: {
      variantId: string;
      slotId: string;
      modifierId: string;
      weightId: string;
    };
  }>;
}

/**
 * Turns one scope group into a request body. Operations are unwrapped to their minimal form and mapped
 * to the API shape here, at the boundary - resolveOperationPlan stays free of payload concerns.
 */
export const toPatchJobRequest = (group: OperationPlanGroup): PatchJobRequestDto => {
  const setOperations = mapPatchOperations(group.setOperations.map((entry) => entry.minimal ?? entry));
  const removeOperations = mapPatchOperations(
    group.removeOperations.map((entry) => entry.minimal ?? entry),
    true
  );

  const request: PatchJobRequestDto = { patchIds: group.patchIds };
  if (setOperations.length > 0) request.setOperations = setOperations;
  if (removeOperations.length > 0) request.removeOperations = removeOperations;

  return request;
};

/**
 * Job ids out of a submission response. The endpoint has three shapes in the wild: a bare array of
 * jobs, an object wrapping a `jobs` array, and a single job whose `jobId` may itself be a
 * comma-separated list.
 */
export const extractJobIds = (submitResult: any): string[] => {
  if (Array.isArray(submitResult)) {
    return submitResult.map((job: JobResponse) => job.jobId.toString());
  }

  if (submitResult?.jobs && Array.isArray(submitResult.jobs)) {
    return submitResult.jobs.map((job: JobResponse) => job.jobId.toString());
  }

  const { jobId } = (submitResult || {}) as JobResponse;
  if (!jobId) return [];

  return jobId
    .toString()
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);
};

interface SubmitStatus {
  isSubmitting: boolean;
  success: boolean;
  error: string | null;
  jobStatus?: {
    jobId?: number | string;
    jobIds?: string[];
    status: string;
    message?: string;
    progress?: number;
    total?: number;
    totalSubJobs?: number;
    totalSubJobsCompleted?: number;
    totalSubJobsFailed?: number;
    totalSubJobsInProgress?: number;
    startedAt?: string;
    completedAt?: string;
  };
}

/**
 * Custom hook for handling patch submission logic
 * Manages the submission of pending changes to patches
 */
export const useSubmitPatches = () => {
  const dispatch = useDispatch<AppDispatch>();
  const pendingChanges = useSelector(selectPendingChanges);
  const { data: userData } = useUser(true);
  const currentUser = userData?.data;
  
  const [status, setStatus] = useState<SubmitStatus>({
    isSubmitting: false,
    success: false,
    error: null,
  });

  // No RTK Query mutation, use Axios function
  const isLoading = status.isSubmitting;

  /**
   * Reset the status to initial state
   */
  const resetStatus = useCallback(() => {
    setStatus({
      isSubmitting: false,
      success: false,
      error: null,
    });
  }, []);

  /**
   * Polls the patch job status using the jobId (Axios version)
   */
  const pollJobStatus = useCallback(
    async (jobId: number | string): Promise<PatchJobStatusResponse> => {
      // Use the Axios-based getPatchJobStatus function
      return pollPatchJobStatus(
        jobId.toString(),
        async (id: string) => {
          return await (await import('../../../services/api')).getPatchJobStatus(id);
        },
        {
          pollingInterval: 2000,
          timeout: 60000,
          onProgress: (jobStatus) => {
            setStatus((prevStatus) => ({
              ...prevStatus,
              jobStatus: {
                jobId: jobStatus.jobId,
                status: jobStatus.status,
                message: jobStatus.message,
                totalSubJobs: jobStatus.totalSubJobs,
                totalSubJobsCompleted: jobStatus.totalSubJobsCompleted,
                totalSubJobsFailed: jobStatus.totalSubJobsFailed,
                totalSubJobsInProgress: jobStatus.totalSubJobsInProgress,
                startedAt: jobStatus.startedAt,
                completedAt: jobStatus.completedAt,
              },
            }));
          },
          onComplete: (jobStatus) => {
            console.log(`Job ${jobId} completed with status: ${jobStatus.status}`);
          },
          onError: (error) => {
            console.error(`Error polling job ${jobId}:`, error);
            setStatus((prevStatus) => ({
              ...prevStatus,
              error: `Error polling job: ${error.message}`,
            }));
          },
        }
      );
    },
    []
  );

  /**
   * Polls multiple patch jobs in parallel and aggregates their status
   */
  const pollMultipleJobsStatus = useCallback(
    async (jobIds: string[]): Promise<AggregatedPatchJobStatus> => {
      return pollMultiplePatchJobs(
        jobIds,
        async (id: string) => {
          return await (await import('../../../services/api')).getPatchJobStatus(id);
        },
        {
          pollingInterval: 2000,
          timeout: 60000,
          onProgress: (aggregatedStatus) => {
            setStatus((prevStatus) => ({
              ...prevStatus,
              jobStatus: {
                jobIds: aggregatedStatus.jobIds,
                status: aggregatedStatus.overallStatus,
                message: aggregatedStatus.message,
                progress: aggregatedStatus.totalSubJobsCompleted,
                total: aggregatedStatus.totalSubJobs,
                totalSubJobs: aggregatedStatus.totalSubJobs,
                totalSubJobsCompleted: aggregatedStatus.totalSubJobsCompleted,
                totalSubJobsFailed: aggregatedStatus.totalSubJobsFailed,
                totalSubJobsInProgress: aggregatedStatus.totalSubJobsInProgress,
                startedAt: aggregatedStatus.startedAt,
                completedAt: aggregatedStatus.completedAt,
              },
            }));
          },
          onComplete: (aggregatedStatus) => {
            console.log(`All jobs completed with overall status: ${aggregatedStatus.overallStatus}`);
          },
          onError: (error) => {
            console.error(`Error polling multiple jobs:`, error);
            setStatus((prevStatus) => ({
              ...prevStatus,
              error: `Error polling jobs: ${error.message}`,
            }));
          },
        }
      );
    },
    []
  );

  /**
   * Submit the pending changes to the patches API.
   *
   * Store-level edits mean different patches receive different operations, but a request applies every
   * operation to every patch it names. So one submission can become several requests - one per group of
   * patches sharing an effective operation set - whose job ids are then polled together.
   */
  const submitPatches = useCallback(async () => {
    const { groups } = resolveOperationPlan(pendingChanges.data || {});

    // Check if there are pending changes to submit
    if (!pendingChanges.id || !groups.length) {
      const errorMsg = 'No pending changes to submit';
      console.error(errorMsg);
      setStatus({
        isSubmitting: false,
        success: false,
        error: errorMsg,
      });
      return;
    }

    setStatus({
      isSubmitting: true,
      success: false,
      error: null,
    });

    try {
      // Check if user is available
      if (!currentUser?.loginId) {
        throw new Error('User not authenticated or user ID not available');
      }

      const loginId = currentUser.loginId;

      // Each group is submitted separately. Failures are captured rather than thrown so a later group
      // failing does not hide the fact that earlier ones were already accepted by the backend.
      const outcomes = await promisePool(
        groups.map((group) => async () => {
          try {
            return { ok: true as const, result: await submitPatchJobs(toPatchJobRequest(group), loginId) };
          } catch (error) {
            return { ok: false as const, error };
          }
        }),
        SUBMIT_CONCURRENCY
      );

      const failures = outcomes.filter((outcome) => !outcome.ok);

      if (failures.length) {
        const submitted = groups.length - failures.length;
        const firstError = (failures[0] as { error: unknown }).error;
        const detail = firstError instanceof Error ? firstError.message : String(firstError);

        // Pending changes are deliberately kept: the operations that did go through are recorded on the
        // patches already, and keeping the list is the only way for the user to retry the rest.
        throw new Error(
          `Submitted ${submitted} of ${groups.length} operation group(s) before failing: ${detail}. ` +
            'Pending changes have been kept so the remainder can be retried.'
        );
      }

      const jobIds = outcomes.flatMap((outcome) => extractJobIds((outcome as { result: any }).result));

      if (!jobIds.length) {
        throw new Error('No job ID returned from patch submission');
      }

      // A single job keeps the single-job status shape, exactly as before this fanned out.
      const finalJobStatus: PatchJobStatusResponse | AggregatedPatchJobStatus =
        jobIds.length === 1 ? await pollJobStatus(jobIds[0]) : await pollMultipleJobsStatus(jobIds);

      // Clear the pending changes only if the job(s) completed successfully
      const isSuccessful = 'overallStatus' in finalJobStatus
        ? finalJobStatus.overallStatus === 'COMPLETED'
        : finalJobStatus.status === 'COMPLETED';

      if (isSuccessful) {
        dispatch(clearPendingChanges());
        // The patches now differ from any copy the UI is holding. Anything caching a patch payload -
        // notably the operations editor's "current" and "Was $X" baseline - has to refetch.
        dispatch(patchDataInvalidated());

        setStatus({
          isSubmitting: false,
          success: true,
          error: null,
          jobStatus: 'overallStatus' in finalJobStatus
            ? {
                jobIds: finalJobStatus.jobIds,
                status: finalJobStatus.overallStatus,
                message: finalJobStatus.message,
                progress: finalJobStatus.totalSubJobsCompleted,
                total: finalJobStatus.totalSubJobs,
                totalSubJobs: finalJobStatus.totalSubJobs,
                totalSubJobsCompleted: finalJobStatus.totalSubJobsCompleted,
                totalSubJobsFailed: finalJobStatus.totalSubJobsFailed,
                totalSubJobsInProgress: finalJobStatus.totalSubJobsInProgress,
                startedAt: finalJobStatus.startedAt,
                completedAt: finalJobStatus.completedAt,
              }
            : {
                jobId: finalJobStatus.jobId,
                status: finalJobStatus.status,
                message: finalJobStatus.message,
                totalSubJobs: finalJobStatus.totalSubJobs,
                totalSubJobsCompleted: finalJobStatus.totalSubJobsCompleted,
                totalSubJobsFailed: finalJobStatus.totalSubJobsFailed,
                totalSubJobsInProgress: finalJobStatus.totalSubJobsInProgress,
                startedAt: finalJobStatus.startedAt,
                completedAt: finalJobStatus.completedAt,
              },
        });
      } else {
        const status = 'overallStatus' in finalJobStatus ? finalJobStatus.overallStatus : finalJobStatus.status;
        const message = finalJobStatus.message || '';
        const errorMsg = `Patch job(s) failed with status: ${status}. ${message}`;
        console.error(errorMsg);
        throw new Error(errorMsg);
      }
    } catch (error) {
      console.error('Error submitting patches:', error);
      setStatus({
        isSubmitting: false,
        success: false,
        error: error instanceof Error ? error.message : 'Failed to submit changes',
      });
      throw error; // Re-throw so the calling code can handle it
    }
  }, [pendingChanges, submitPatchJobs, dispatch, pollJobStatus, pollMultipleJobsStatus, currentUser]);

  return {
    submitPatches,
    resetStatus,
    isSubmitting: status.isSubmitting || isLoading,
    success: status.success,
    error: status.error,
    jobStatus: status.jobStatus,
    hasPendingChanges: !!(
      pendingChanges.id &&
      (pendingChanges.data.setOperations.length > 0 || 
       pendingChanges.data.removeOperations.length > 0)
    ),
  };
};
